/**
 * fontLoader.js
 * Utilities for optimizing font loading and rendering
 */

/**
 * Configuration for font loading
 */
const config = {
  fontDisplay: 'swap', // 'auto', 'block', 'swap', 'fallback', 'optional'
  timeout: 3000, // ms
  criticalFonts: [],
  fontObserverOptions: {
    weight: 'normal',
    style: 'normal',
    stretch: 'normal'
  }
};

/**
 * Preloads fonts to improve rendering performance
 * @param {Array<string>} fontUrls - Array of font URLs to preload
 * @param {string} fontDisplay - Font-display strategy
 */
export const preloadFonts = (fontUrls = [], fontDisplay = config.fontDisplay) => {
  if (!fontUrls.length) return;
  
  fontUrls.forEach(url => {
    const link = document.createElement('link');
    link.rel = 'preload';
    link.href = url;
    link.as = 'font';
    link.type = 'font/woff2'; // Adjust based on your font type
    link.crossOrigin = 'anonymous';
    
    document.head.appendChild(link);
  });
  
  // Add font-display to style element
  const style = document.createElement('style');
  style.textContent = `
    @font-face {
      font-display: ${fontDisplay};
    }
  `;
  
  document.head.appendChild(style);
};

/**
 * Loads a font and returns a promise that resolves when the font is loaded
 * @param {string} fontFamily - The font family name
 * @param {Object} options - Font loading options
 * @returns {Promise<void>} - Promise that resolves when the font is loaded
 */
export const loadFont = (fontFamily, options = {}) => {
  const {
    weight = config.fontObserverOptions.weight,
    style = config.fontObserverOptions.style,
    stretch = config.fontObserverOptions.stretch,
    timeout = config.timeout
  } = options;
  
  // Use the Font Loading API if available
  if ('fonts' in document) {
    return document.fonts.load(`${weight} ${style} ${stretch} 1em ${fontFamily}`)
      .then(() => {
        console.log(`Font loaded: ${fontFamily}`);
      })
      .catch(error => {
        console.warn(`Error loading font ${fontFamily}:`, error);
      });
  }
  
  // Fallback for browsers without Font Loading API
  return new Promise((resolve) => {
    const sampleText = 'abcdefghijklmnopqrstuvwxyz';
    
    // Create a span with the font applied
    const span = document.createElement('span');
    span.style.position = 'absolute';
    span.style.top = '-9999px';
    span.style.left = '-9999px';
    span.style.visibility = 'hidden';
    span.style.fontFamily = `${fontFamily}, monospace`;
    span.style.fontWeight = weight;
    span.style.fontStyle = style;
    span.style.fontStretch = stretch;
    span.style.fontSize = '100px';
    span.textContent = sampleText;
    
    document.body.appendChild(span);
    
    // Create a reference span with monospace
    const referenceSpan = document.createElement('span');
    referenceSpan.style.position = 'absolute';
    referenceSpan.style.top = '-9999px';
    referenceSpan.style.left = '-9999px';
    referenceSpan.style.visibility = 'hidden';
    referenceSpan.style.fontFamily = 'monospace';
    referenceSpan.style.fontSize = '100px';
    referenceSpan.textContent = sampleText;
    
    document.body.appendChild(referenceSpan);
    
    // Check if widths are different (font loaded)
    const checkWidth = () => {
      if (span.offsetWidth !== referenceSpan.offsetWidth) {
        // Font loaded
        document.body.removeChild(span);
        document.body.removeChild(referenceSpan);
        console.log(`Font loaded: ${fontFamily}`);
        resolve();
        return true;
      }
      return false;
    };
    
    // Check immediately
    if (checkWidth()) return;
    
    // Set up polling
    const startTime = Date.now();
    const interval = setInterval(() => {
      if (checkWidth() || Date.now() - startTime > timeout) {
        clearInterval(interval);
        if (Date.now() - startTime > timeout) {
          // Timeout reached
          document.body.removeChild(span);
          document.body.removeChild(referenceSpan);
          console.warn(`Font loading timed out: ${fontFamily}`);
          resolve();
        }
      }
    }, 50);
  });
};

/**
 * Adds font-display CSS to improve font rendering
 * @param {string} fontDisplay - The font-display value
 */
export const addFontDisplayCSS = (fontDisplay = config.fontDisplay) => {
  const style = document.createElement('style');
  style.textContent = `
    @font-face {
      font-display: ${fontDisplay};
    }
  `;
  
  document.head.appendChild(style);
};

/**
 * Loads multiple fonts in parallel
 * @param {Array<Object>} fonts - Array of font objects with fontFamily and options
 * @returns {Promise<void>} - Promise that resolves when all fonts are loaded
 */
export const loadFonts = async (fonts = []) => {
  if (!fonts.length) return;
  
  const loadPromises = fonts.map(font => {
    return loadFont(font.fontFamily, font.options);
  });
  
  try {
    await Promise.all(loadPromises);
    console.log('All fonts loaded successfully');
  } catch (error) {
    console.warn('Error loading fonts:', error);
  }
};

/**
 * Adds a class to the document when fonts are loaded
 * @param {Array<Object>} fonts - Array of font objects with fontFamily and options
 * @param {string} className - Class name to add when fonts are loaded
 */
export const addFontsLoadedClass = async (fonts = [], className = 'fonts-loaded') => {
  try {
    await loadFonts(fonts);
    document.documentElement.classList.add(className);
  } catch (error) {
    console.warn('Error in font loading class:', error);
    // Add class anyway after timeout to ensure UI is usable
    setTimeout(() => {
      document.documentElement.classList.add(className);
    }, config.timeout);
  }
};

/**
 * Sets up font loading for the application
 * @param {Object} options - Font loading configuration
 */
export const setupFontLoading = (options = {}) => {
  const {
    criticalFonts = config.criticalFonts,
    fontDisplay = config.fontDisplay,
    className = 'fonts-loaded'
  } = options;
  
  // Add font-display CSS
  addFontDisplayCSS(fontDisplay);
  
  // Preload critical fonts
  if (criticalFonts.length) {
    preloadFonts(criticalFonts, fontDisplay);
  }
  
  // Add fonts-loaded class when fonts are loaded
  if (options.fonts && options.fonts.length) {
    addFontsLoadedClass(options.fonts, className);
  }
};

/**
 * Creates a font face observer for a specific font
 * @param {string} fontFamily - The font family name
 * @param {Object} options - Font observer options
 * @returns {Object} - Font observer object
 */
export const createFontObserver = (fontFamily, options = {}) => {
  const {
    weight = config.fontObserverOptions.weight,
    style = config.fontObserverOptions.style,
    stretch = config.fontObserverOptions.stretch,
    testString = 'BESbswy',
    timeout = config.timeout
  } = options;
  
  return {
    load: () => loadFont(fontFamily, { weight, style, stretch, timeout }),
    fontFamily,
    options: { weight, style, stretch, testString, timeout }
  };
};

/**
 * Default export with all font loading utilities
 */
export default {
  preloadFonts,
  loadFont,
  loadFonts,
  addFontDisplayCSS,
  addFontsLoadedClass,
  setupFontLoading,
  createFontObserver,
  config
};